import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import type { CSSProperties, KeyboardEvent as ReactKeyboardEvent } from 'react';
import { Link } from 'react-router-dom';
import { api_error_reason } from '../sales_team/api';
import type { TeamMember } from '../sales_team/api';
import { assign_task, get_roster } from './claim_api';
import { assignable_members, unconfigured_count, unconfigured_hint } from './claim_hold';
import type { AssignResult } from './claim_hold';
import { MENU_WIDTH, menu_position } from './assign_menu_position';
import type { MenuPos } from './assign_menu_position';

interface Props {
  task_uuid: string;
  on_assigned: (res: AssignResult, member: TeamMember) => void;
  on_error?: (message: string) => void;
  label?: string;
}

const CHIP: CSSProperties = {
  display: 'inline-block',
  borderRadius: 9999,
  padding: '0 6px',
  fontSize: 11,
  fontWeight: 500,
  lineHeight: '18px'
};

const LEVEL_CHIP: CSSProperties = {
  ...CHIP,
  background: 'var(--brand-primary-subtle-bg)',
  color: 'var(--brand-primary-subtle-text)'
};
const WARN_CHIP: CSSProperties = {
  ...CHIP,
  background: 'var(--state-warning-bg)',
  color: 'var(--state-warning-text)'
};
const MUTED_CHIP: CSSProperties = {
  ...CHIP,
  background: 'var(--surface-sunken)',
  color: 'var(--text-muted)'
};

function AssignPicker({ task_uuid, on_assigned, on_error, label = 'Assign' }: Props) {
  const [open, set_open] = useState(false);
  const [members, set_members] = useState<TeamMember[] | null>(null);
  const [load_error, set_load_error] = useState('');
  const [query, set_query] = useState('');
  const [busy, set_busy] = useState('');
  const [pos, set_pos] = useState<MenuPos | null>(null);
  const root_ref = useRef<HTMLDivElement>(null);
  const trigger_ref = useRef<HTMLButtonElement>(null);
  const search_ref = useRef<HTMLInputElement>(null);
  const option_refs = useRef<Array<HTMLButtonElement | null>>([]);

  useLayoutEffect(() => {
    if (!open) return undefined;
    const place = () => {
      if (trigger_ref.current) set_pos(menu_position(trigger_ref.current));
    };
    place();
    window.addEventListener('resize', place);
    window.addEventListener('scroll', place, true);
    return () => {
      window.removeEventListener('resize', place);
      window.removeEventListener('scroll', place, true);
    };
  }, [open]);

  useEffect(() => {
    if (!open) return undefined;
    let live = true;
    set_load_error('');
    get_roster()
      .then((m) => live && set_members(m))
      .catch((e: unknown) => live && set_load_error(api_error_reason(e, 'Could not load reps')));
    const on_down = (e: MouseEvent) => {
      if (root_ref.current && !root_ref.current.contains(e.target as Node)) set_open(false);
    };
    const on_key = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return;
      set_open(false);
      trigger_ref.current?.focus();
    };
    document.addEventListener('mousedown', on_down);
    document.addEventListener('keydown', on_key);
    return () => {
      live = false;
      document.removeEventListener('mousedown', on_down);
      document.removeEventListener('keydown', on_key);
    };
  }, [open]);

  const shown = useMemo(() => assignable_members(members || [], query), [members, query]);
  const unconfigured = useMemo(() => unconfigured_count(members || []), [members]);

  // ArrowDown/ArrowUp walk search box -> options; ArrowUp past the first option
  // returns to the search box.
  const focus_option = (i: number) => {
    if (i < 0) {
      search_ref.current?.focus();
      return;
    }
    option_refs.current[Math.min(i, shown.length - 1)]?.focus();
  };
  const on_search_key = (e: ReactKeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'ArrowDown' && shown.length) {
      e.preventDefault();
      focus_option(0);
    }
  };
  const on_option_key = (e: ReactKeyboardEvent<HTMLButtonElement>, i: number) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      focus_option(i + 1);
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      focus_option(i - 1);
    }
  };

  const pick = (m: TeamMember) => {
    if (busy) return;
    set_busy(m.ops_uuid);
    assign_task(task_uuid, m.ops_uuid)
      .then((res) => {
        set_open(false);
        set_query('');
        trigger_ref.current?.focus();
        on_assigned(res, m);
      })
      .catch((e: unknown) => on_error?.(api_error_reason(e, 'Could not assign the task')))
      .finally(() => set_busy(''));
  };

  return (
    <div ref={root_ref} style={{ position: 'relative', display: 'inline-block' }}>
      <button
        ref={trigger_ref}
        type="button"
        aria-haspopup="listbox"
        aria-expanded={open}
        onClick={() => set_open((o) => !o)}
        className="rounded-md border px-3 py-1 text-xs font-medium"
        style={{
          margin: 0,
          background: 'var(--surface-card)',
          borderColor: 'var(--border-default)',
          color: 'var(--text-primary)'
        }}
      >
        {label}
      </button>
      {open ? (
        <div
          role="dialog"
          aria-label="Assign task"
          className="rounded-md border shadow-lg"
          style={{
            position: 'fixed',
            ...(pos?.bottom !== undefined ? { bottom: pos.bottom } : { top: pos?.top ?? 0 }),
            left: pos?.left ?? 0,
            visibility: pos ? 'visible' : 'hidden',
            zIndex: 1000,
            width: MENU_WIDTH,
            background: 'var(--surface-card)',
            borderColor: 'var(--border-default)'
          }}
        >
          <div className="p-2 border-b" style={{ borderColor: 'var(--border-default)' }}>
            <input
              ref={search_ref}
              // eslint-disable-next-line jsx-a11y/no-autofocus
              autoFocus
              type="search"
              onKeyDown={on_search_key}
              value={query}
              onChange={(e) => set_query(e.target.value)}
              placeholder="Search reps"
              aria-label="Search reps"
              className="w-full rounded-md border px-2 py-1 text-sm"
              style={{
                background: 'var(--input-bg)',
                borderColor: 'var(--input-border)',
                color: 'var(--input-text)'
              }}
            />
          </div>
          <ul role="listbox" style={{ maxHeight: 260, overflowY: 'auto', margin: 0, padding: 0 }}>
            {load_error ? (
              <li className="px-3 py-2 text-xs" style={{ color: 'var(--state-error)' }}>
                {load_error}
              </li>
            ) : null}
            {!load_error && !members ? (
              <li className="px-3 py-2 text-xs" style={{ color: 'var(--text-muted)' }}>
                Loading reps…
              </li>
            ) : null}
            {members && shown.length === 0 ? (
              <li className="px-3 py-2 text-xs" style={{ color: 'var(--text-muted)' }}>
                No matching active reps
              </li>
            ) : null}
            {shown.map((m, i) => (
              <li
                key={m.ops_uuid}
                role="option"
                aria-selected={false}
                style={{ listStyle: 'none' }}
              >
                <button
                  ref={(el) => {
                    option_refs.current[i] = el;
                  }}
                  type="button"
                  disabled={!!busy}
                  onClick={() => pick(m)}
                  onKeyDown={(e) => on_option_key(e, i)}
                  className="flex w-full items-center gap-2 px-3 py-2 text-left text-sm"
                  style={{
                    margin: 0,
                    background: 'transparent',
                    border: 'none',
                    color: 'var(--text-primary)',
                    cursor: busy ? 'wait' : 'pointer'
                  }}
                >
                  <span className="flex-1 truncate">
                    {busy === m.ops_uuid ? 'Assigning…' : m.name || m.email || m.ops_uuid}
                  </span>
                  {m.level ? <span style={LEVEL_CHIP}>{m.level}</span> : null}
                  {m.on_leave_today ? <span style={WARN_CHIP}>On leave</span> : null}
                  {!m.on_shift_now ? <span style={MUTED_CHIP}>Off shift</span> : null}
                </button>
              </li>
            ))}
          </ul>
          {unconfigured > 0 ? (
            <div
              className="px-3 py-2 border-t text-xs"
              data-testid="assign-unconfigured-hint"
              style={{ borderColor: 'var(--border-default)', color: 'var(--text-muted)' }}
            >
              <Link to="/sales-team" style={{ color: 'var(--brand-primary)' }}>
                {unconfigured_hint(unconfigured)}
              </Link>
            </div>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}

export default AssignPicker;
